import { Schema } from "effect";
import {
  AccountId,
  ChannelThreadId,
  ShopperAiIntegrationId,
} from "../../../ids/definitions";
import { DateFromDb } from "../../../schema/helpers";
import { MessageChannel } from "./message";
import { ConversationProduct } from "./product";
import {
  ChatFlowThreadChannelData,
  EmailThreadChannelData,
  MetaCommentThreadChannelData,
  MobileMessageThreadChannelData,
  SmsThreadChannelData,
  ThreadAnalyticsData,
  VoiceThreadChannelData,
} from "./thread/channel-data";

const threadBaseFields = {
  id: ChannelThreadId,
  integrationId: ShopperAiIntegrationId,
  accountId: AccountId,
  externalThreadId: Schema.String,
  channel: MessageChannel,
  products: Schema.Array(ConversationProduct),
  // DateFromDb accepts both an ISO string (fresh off the wire) and a Date
  // instance (already-decoded), so the collection's schema re-validation on
  // writeUpsert doesn't reject the client-side value.
  createdAt: DateFromDb,
  analyticsData: Schema.NullOr(ThreadAnalyticsData),
};

const EmailChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Union(
    Schema.Literal("gmail"),
    Schema.Literal("outlook"),
  ),
  channelData: Schema.NullOr(EmailThreadChannelData),
});

const MetaCommentChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Union(
    Schema.Literal("facebook"),
    Schema.Literal("instagram"),
  ),
  channelData: Schema.NullOr(MetaCommentThreadChannelData),
});

const ChatFlowChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Literal("redoChatFlow"),
  channelData: Schema.NullOr(ChatFlowThreadChannelData),
});

const SmsChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Literal("telnyx_sms", "imessage", "sms_phone_number"),
  channelData: Schema.NullOr(SmsThreadChannelData),
});

const MobileMessageChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Literal("mobileMessage"),
  channel: Schema.Literal("mobileMessage"),
  channelData: Schema.NullOr(MobileMessageThreadChannelData),
});

const VoiceChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Literal("telnyx_voice"),
  channelData: Schema.NullOr(VoiceThreadChannelData),
});

const NullChannelChannelThread = Schema.Struct({
  ...threadBaseFields,
  integrationType: Schema.Union(
    Schema.Literal("tiktok"),
    Schema.Literal("attentive"),
    Schema.Literal("postscript"),
    Schema.Literal("redoChatAi"),
    Schema.Literal("redoChatLive"),
    Schema.Literal("contactForm"),
  ),
  channelData: Schema.Null,
});

export const ChannelThread = Schema.Union(
  EmailChannelThread,
  MetaCommentChannelThread,
  ChatFlowChannelThread,
  SmsChannelThread,
  MobileMessageChannelThread,
  VoiceChannelThread,
  NullChannelChannelThread,
);
export type ChannelThread = typeof ChannelThread.Type;

export const ChannelThreadCursor = Schema.Struct({
  id: ChannelThreadId,
  createdAt: DateFromDb,
});
export type ChannelThreadCursor = typeof ChannelThreadCursor.Type;
